<template>
  <Sidebar
    app-name="Campus"
    :navigation-links="appNavLinks"
    :is-blurred="isBlurred"
  >
    <template #before-nav>
      <SidebarContextSection
        v-if="showExpeditionSection"
        :icon="PhPersonSimpleHike"
        :icon-size="22"
        :selected-id="selectedExpeditionId"
        :options="sortedExpeditions"
        :display-name="formattedExpeditionName"
        :links="expeditionLinks"
        :is-link-active="isExpeditionLinkActive"
        @update:selected-id="selectedExpeditionId = $event"
        @link-click="closeMobileSidebar"
      />

      <SidebarContextSection
        v-if="showPodSection"
        :icon="Users"
        icon-class="w-5 h-5 text-gray-600"
        :selected-id="selectedPodId"
        :options="podsStore.pods"
        :display-name="podName"
        :links="podLinks"
        :is-link-active="isPodLinkActive"
        @update:selected-id="selectedPodId = $event"
        @link-click="closeMobileSidebar"
      />
    </template>

    <template #extra>
      <StatusMessages class="mt-2" />
    </template>
  </Sidebar>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, type Component, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Users } from 'lucide-vue-next'
import { PhPersonSimpleHike } from '@phosphor-icons/vue'
import { Sidebar } from '@outtalent-dev/shared'

import { useAuth } from '@/composables/useAuth'
import { usePrivacyMode } from '@/composables/usePrivacyMode'
import { useExpeditionsStore } from '@/stores/expeditions'
import { usePodsStore } from '@/stores/pods'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import StatusMessages from '@/components/common/StatusMessages.vue'
import SidebarContextSection from '@/components/layout/SidebarContextSection.vue'
import {
  buildContextNavLinks,
  expeditionAdminLinks,
  fellowLinks,
  graduationLink,
  myExpeditionsLink,
  myPodsLink,
  MY_EXPEDITIONS_MATCHER,
  MY_PODS_MATCHER,
  podAdminLinks,
  tracksLink,
  type AppLink,
  type ContextNavLink,
} from '@/const/sidebarLinks'

interface SidebarNavLink {
  name: string
  icon: Component
  to: string
  active: boolean
}

const NON_EXPEDITION_MANAGE_SEGMENTS = new Set([
  'fellows',
  'fellow',
  'reports',
  'program-statuses',
  'internal-comments',
  'create',
  'create-pod',
  'review-history',
  'pod',
  'tracks',
  'onboarding',
  'graduation',
])

const route = useRoute()
const router = useRouter()
const expeditionsStore = useExpeditionsStore()
const podsStore = usePodsStore()
const { isBlurred } = usePrivacyMode()
const { isGuide, isFellow, isAdmin } = useAuth()
const isMobileSidebarOpen = inject<Ref<boolean>>('isMobileSidebarOpen')
const isSidebarCollapsed = inject<Ref<boolean>>('isSidebarCollapsed')

const selectedExpeditionId = ref<string | null>(null)
const selectedPodId = ref<string | null>(null)

const isFellowProfileRoute = computed(() =>
  route.matched.some((r) => r.name === 'ManageFellowProfileView')
)
const isPodRoute = computed(() => route.path.startsWith('/manage/pod/'))
const isTracksRoute = computed(() => route.path.startsWith('/manage/tracks'))

const currentPodId = computed(() => {
  if (!isPodRoute.value) return null
  const match = route.path.match(/^\/manage\/pod\/([^/]+)/)
  return match ? match[1] : null
})

const currentExpeditionId = computed(() => {
  if (isPodRoute.value || isFellowProfileRoute.value || isTracksRoute.value) {
    return null
  }

  const paramId = route.params.id
  if (typeof paramId === 'string' && paramId.length > 0) {
    return paramId
  }

  const match = route.path.match(/^\/manage\/([^/]+)/)
  if (!match) return null
  const candidate = match[1]
  return NON_EXPEDITION_MANAGE_SEGMENTS.has(candidate) ? null : candidate
})

const isPodsContextActive = computed(() => {
  if (route.path === '/manage') {
    const tab = route.query.tab
    return tab === 'pod' || tab === 'cohort'
  }
  return /^\/manage\/pod\/\d+/.test(route.path)
})

const baseAppLinks = computed<AppLink[]>(() => {
  if (isFellow.value) return fellowLinks
  if (!isGuide.value) return []

  const primary = isPodsContextActive.value ? myPodsLink : myExpeditionsLink
  const adminTail = isAdmin.value
    ? isPodsContextActive.value
      ? podAdminLinks
      : expeditionAdminLinks
    : []

  return [
    primary,
    ...(isAdmin.value ? [tracksLink] : []),
    graduationLink,
    ...adminTail,
  ]
})

const appNavLinks = computed<SidebarNavLink[]>(() =>
  baseAppLinks.value.map((link) => ({
    name: link.name,
    icon: link.icon,
    to: link.to,
    active: isActiveRoute(link),
  }))
)

const showExpeditionSection = computed(
  () => isGuide.value && !!currentExpeditionId.value && !isSidebarCollapsed?.value
)

const showPodSection = computed(
  () => isGuide.value && !!currentPodId.value && !isSidebarCollapsed?.value
)

const expeditionLinks = computed<ContextNavLink[]>(() => {
  const id = currentExpeditionId.value
  return id ? buildContextNavLinks(`/manage/${id}`) : []
})

const podLinks = computed<ContextNavLink[]>(() => {
  const id = currentPodId.value
  if (!id) return []
  const links = buildContextNavLinks(`/manage/pod/${id}`)
  return isBlurred.value ? links.filter((p) => p.key !== 'members') : links
})

const sortedExpeditions = computed(() =>
  [...expeditionsStore.expeditions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
)

const formattedExpeditionName = computed(() => {
  const selectedId = selectedExpeditionId.value
  const selected = selectedId
    ? expeditionsStore.expeditions.find((e) => e.id.toString() === selectedId)
    : null

  if (selected?.name) return formatExpeditionName(selected.name)

  const progress = expeditionsStore.adventurersProgress as { name?: string } | null
  return formatExpeditionName(progress?.name || 'Select expedition')
})

const podName = computed(() => {
  const id = selectedPodId.value
  const pod = id ? podsStore.pods.find((p) => p.id.toString() === id) : null
  return pod?.name ?? 'Select pod'
})

onMounted(async () => {
  if (!isGuide.value) return

  selectedExpeditionId.value = currentExpeditionId.value
  selectedPodId.value = currentPodId.value

  if (!expeditionsStore.expeditions.length) {
    await expeditionsStore.fetchExpeditions()
  }
  if (isPodRoute.value && podsStore.pods.length === 0) {
    await podsStore.fetchPods()
  }
})

watch(currentExpeditionId, (newId) => {
  selectedExpeditionId.value = newId
})

watch(currentPodId, (newId) => {
  selectedPodId.value = newId
  if (newId && podsStore.pods.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    podsStore.fetchPods()
  }
})

watch(selectedPodId, (newId, oldId) => {
  if (!newId || newId === oldId) return
  const currentId = currentPodId.value
  if (!currentId || newId === currentId) return

  const pathAfterPod = route.path.replace(`/manage/pod/${currentId}`, '')
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace(`/manage/pod/${newId}${pathAfterPod}`)
})

watch(selectedExpeditionId, (newId, oldId) => {
  if (!newId || newId === oldId) return
  const currentId = currentExpeditionId.value
  if (!currentId || newId === currentId) return

  if (route.params.userId) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace(`/manage/${newId}`)
    return
  }

  const pathAfterExpedition = route.path.replace(`/manage/${currentId}`, '')
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace(`/manage/${newId}${pathAfterExpedition}`)
})

function isActiveRoute(link: AppLink) {
  if (link.matcher === MY_EXPEDITIONS_MATCHER) {
    if (route.path === '/manage') {
      const tab = route.query.tab
      return !tab || tab === 'expeditions'
    }
    return !isFellowProfileRoute.value && /^\/manage\/\d+/.test(route.path)
  }
  if (link.matcher === MY_PODS_MATCHER) {
    if (route.path === '/manage') {
      const tab = route.query.tab
      return tab === 'pod' || tab === 'cohort'
    }
    return /^\/manage\/pod\/\d+/.test(route.path)
  }
  return route.path === link.matcher || route.path.startsWith(link.matcher + '/')
}

function isExpeditionLinkActive(link: ContextNavLink) {
  const id = currentExpeditionId.value
  if (!id) return false

  if (link.key === 'program') {
    const programPatterns = [
      new RegExp(`^/manage/${id}$`),
      new RegExp(`^/manage/${id}/\\d+$`),
      new RegExp(`^/manage/${id}/\\d+/fellow-settings$`),
      /^\/manage\/review-history\/user\/\d+$/,
    ]
    return programPatterns.some((p) => p.test(route.path))
  }

  return route.path.startsWith(link.to)
}

function isPodLinkActive(link: ContextNavLink) {
  const id = currentPodId.value
  if (!id) return false

  if (link.key === 'program') {
    return (
      route.path === `/manage/pod/${id}` ||
      new RegExp(`^/manage/pod/${id}/\\d+$`).test(route.path) ||
      new RegExp(`^/manage/pod/${id}/\\d+/fellow-settings$`).test(route.path)
    )
  }
  return route.path === link.to
}

function closeMobileSidebar() {
  if (isMobileSidebarOpen?.value) {
    isMobileSidebarOpen.value = false
  }
}
</script>
